/* eslint-disable react/no-danger */
import classNames from 'classnames';
import uniqBy from 'lodash/uniqBy';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import useSelection from '../../hooks/useSelection';
import useSelectionVenues from '../../hooks/useSelectionVenues';

import FilmCard from '../cards/Film';
import PersonCard from '../cards/Person';
import Title from '../typography/Title';

import styles from '../../../styles/blocks/selection.module.css';

const propTypes = {
    layout: PropTypes.oneOf(['films', 'events', 'comitee']),
    title: PropTypes.string,
    query: PropTypes.shape({}),
    selection: PropTypes.shape({}),
    venues: PropTypes.arrayOf(PropTypes.shape({})),
    padded: PropTypes.bool,
    className: PropTypes.string,
    isContainer: PropTypes.bool,
    containerClassName: PropTypes.string,
};

function SelectionBlock({
    layout = null,
    title = null,
    query = null,
    selection: initialSelection = null,
    venues: initialVenues = null,
    padded = false,
    className = null,
    isContainer = false,
    containerClassName = null,
}) {
    const { selection: querySelection = null, ...props } = query || {};
    const { id = null } = querySelection || {};
    const { data: loadedSelection, isLoading = false } = useSelection(id, { query: props || null });
    const { id: selectionId = null, items = null } = initialSelection || loadedSelection || {};

    const { data: loadedVenues, venuesIsLoading = false } = useSelectionVenues(
        layout === 'venues' ? id : null,
    );

    const finalVenues = loadedVenues || initialVenues || null;

    const films = useMemo(
        () =>
            (items || []).reduce((acc, item) => {
                const { films: itemFilms = null } = item || {};
                if (itemFilms !== null) {
                    return acc.concat(itemFilms);
                }
                return acc;
            }, []),
        [selectionId],
    );

    const sections = useMemo(() => {
        const allSections = (items || []).reduce((acc, item) => {
            const { categories = null } = item || {};
            if (categories !== null && categories.length > 0) {
                return categories.reduce((its, category) => {
                    if (typeof acc[category.handle] === 'undefined') {
                        // eslint-disable-next-line no-param-reassign
                        its[category.handle] = {
                            ...category,
                            items: [],
                        };
                    }
                    return its;
                }, acc);
            }
            return acc;
        }, {});
        (items || []).forEach((item) => {
            const { programmers = null, categories: itemCategories = null } = item || {};
            if (
                programmers !== null &&
                programmers.length > 0 &&
                itemCategories !== null &&
                itemCategories.length > 0
            ) {
                itemCategories.forEach(({ handle: itemCategoryHandle = null }) => {
                    const section = allSections[itemCategoryHandle];
                    section.items = section.items.concat(programmers);
                });
            }
        });
        return allSections;
    }, [selectionId]);

    const features = useMemo(
        () => uniqBy(films || [], 'id').filter(({ type }) => type === 'feature'),
        [films],
    );

    const shorts = useMemo(
        () => uniqBy(films || [], 'id').filter(({ type }) => type === 'short'),
        [films],
    );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.padded]: padded,
                    [className]: className !== null,
                },
            ])}
        >
            {title !== null ? (
                <Title className={styles.title} size="medium" subtitle>
                    {title}
                </Title>
            ) : null}
            {!isLoading && layout === 'films' ? (
                <div
                    id="films"
                    className={classNames([
                        styles.inner,
                        {
                            [styles.isContainer]: isContainer,
                            [containerClassName]: isContainer && containerClassName !== null,
                        },
                    ])}
                >
                    {features !== null && features.length > 0 ? (
                        <div id="features" className={styles.section}>
                            <Title className={styles.title} subtitle>
                                <FormattedMessage
                                    defaultMessage="Feature films"
                                    description="Section title"
                                />
                            </Title>
                            <div className={classNames([styles.grid, styles.largeGrid])}>
                                {(features || []).map((film) => (
                                    <FilmCard
                                        key={`film-${film?.id}`}
                                        className={styles.film}
                                        item={film}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {shorts !== null && shorts.length > 0 ? (
                        <div id="shorts" className={styles.section}>
                            <Title className={styles.title} subtitle>
                                <FormattedMessage
                                    defaultMessage="Short films"
                                    description="Section title"
                                />
                            </Title>
                            <div className={classNames([styles.grid, styles.shorts])}>
                                {(shorts || []).map((film) => (
                                    <FilmCard
                                        key={`film-${film?.id}`}
                                        className={styles.film}
                                        item={film}
                                    />
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            ) : null}
            {!isLoading && layout === 'comitee' ? (
                <div
                    id="commission"
                    className={classNames([
                        styles.inner,
                        {
                            [styles.isContainer]: isContainer,
                            [containerClassName]: isContainer && containerClassName !== null,
                        },
                    ])}
                >
                    <Title className={classNames([styles.section, styles.large, styles.title])}>
                        <FormattedMessage defaultMessage="Commission" description="Section title" />
                    </Title>
                    {Object.keys(sections).map((handle) => {
                        const { label = null, items: sectionItems = null } = sections[handle] || {};
                        return (
                            <div
                                key={`section-${label}`}
                                className={classNames([styles.section, styles.large])}
                            >
                                <Title className={styles.title} size="small" subtitle>
                                    <FormattedMessage
                                        defaultMessage="Programmers"
                                        description="Section title"
                                    />
                                    {' — '}
                                    {label}
                                </Title>
                                <div className={classNames([styles.grid, styles.programmers])}>
                                    {uniqBy(sectionItems || [], 'id').map((person, idx) => (
                                        <PersonCard
                                            key={`person-${person?.id}-${idx + 1}`}
                                            item={person}
                                        />
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : null}
            {!venuesIsLoading && layout === 'venues' ? (
                <div
                    id="venues"
                    className={classNames([
                        styles.inner,
                        {
                            [styles.isContainer]: isContainer,
                            [containerClassName]: isContainer && containerClassName !== null,
                        },
                    ])}
                >
                    <Title className={classNames([styles.section, styles.large, styles.title])}>
                        <FormattedMessage defaultMessage="Venues" description="Section title" />
                    </Title>
                    <div className={styles.venues}>
                        {(finalVenues || []).map((venue) => {
                            const { id: venueId = null, name = null, url = null } = venue || {};
                            return url !== null ? (
                                <a
                                    className={styles.venue}
                                    href={url}
                                    key={`venue-${venueId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {name}
                                </a>
                            ) : (
                                <div key={`venue-${venueId}`} className={styles.venue}>
                                    {name}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </div>
    );
}

SelectionBlock.propTypes = propTypes;

export default SelectionBlock;
